import React, { createContext, useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './ModalProvider.css';

const ModalContext = createContext(null);

export function ModalProvider({ children }) {
  const { t } = useTranslation();
  const [modal, setModal] = useState({ open: false, title: '', message: null, onConfirm: null, onClose: null, confirmText: 'OK', cancelText: 'Cancelar', showCancel: false });

  const showAlert = useCallback(({ title = '', message = '', onClose = null, confirmText } = {}) => {
    confirmText = confirmText ?? t('modal_aceptar', 'OK');
    return new Promise((resolve) => {
      setModal({
        open: true,
        title,
        message,
        onConfirm: () => {
          setModal(m => ({ ...m, open: false }));
          if (onClose) onClose();
          resolve(true);
        },
        onClose: () => {
          setModal(m => ({ ...m, open: false }));
          resolve(false);
        },
        confirmText,
        showCancel: false,
      });
    });
  }, [t]);

  const showConfirm = useCallback(({ title = '', message = '', onConfirm = null, confirmText, cancelText } = {}) => {
    confirmText = confirmText ?? t('modal_confirmar', 'Aceptar');
    cancelText = cancelText ?? t('btn_cancelar', 'Cancelar');
    return new Promise((resolve) => {
      setModal({
        open: true,
        title,
        message,
        onConfirm: () => {
          setModal(m => ({ ...m, open: false }));
          if (onConfirm) onConfirm();
          resolve(true);
        },
        onClose: () => {
          setModal(m => ({ ...m, open: false }));
          resolve(false);
        },
        confirmText,
        cancelText,
        showCancel: true,
      });
    });
  }, [t]);

  return (
    <ModalContext.Provider value={{ showAlert, showConfirm }}>
      {children}
      {modal.open && (
        <div className="modal-overlay" role="dialog" aria-modal="true">
          <div className="modal-card">
            {modal.title && <h3 className="modal-title">{modal.title}</h3>}
            <div className="modal-body">{typeof modal.message === 'string' ? <p>{modal.message}</p> : modal.message}</div>
            <div className="modal-actions">
              {modal.showCancel && (
                <button className="btn btn-minimal" onClick={() => modal.onClose && modal.onClose()}>{modal.cancelText}</button>
              )}
              <button className="btn btn-primary" onClick={() => modal.onConfirm && modal.onConfirm()}>{modal.confirmText}</button>
            </div>
          </div>
        </div>
      )}
    </ModalContext.Provider>
  );
}

export function useModalContext() {
  const ctx = useContext(ModalContext);
  if (!ctx) throw new Error('useModalContext must be used within ModalProvider');
  return ctx;
}

export default ModalProvider;
